/**
 * Page Element Schema
 * A container element that organizes content blocks into sections with configurable layouts
 */

import type { LayoutConfig } from '@ai-content-hub/content-core';
import type { BlockContent } from '../block/types';
import { registerSchemaI18n } from '../i18n/registerSchemaI18n';

export const defaultPageElementI18n = {
    en: {
        title: 'Title',
        optionalSectionTitle: 'Optional section title',
        addSection: 'Add section',
        pageDescriptionOptional: 'Description (optional)',
        pageTitlePlaceholder: 'Page title',
        pageDescriptionPlaceholder: 'Page description',
        pageSections: 'Content sections',
        pageNoSectionsYet:
            'No content sections yet. Add the first section to get started.',
    },
    vi: {
        title: 'Tiêu đề',
        optionalSectionTitle: 'Tiêu đề mục (tuỳ chọn)',
        addSection: 'Thêm mục',
        pageDescriptionOptional: 'Mô tả (tuỳ chọn)',
        pageTitlePlaceholder: 'Tiêu đề trang',
        pageDescriptionPlaceholder: 'Mô tả trang',
        // EAA-2031: content sections, not questions — see content-core i18n.
        pageSections: 'Phần nội dung',
        pageNoSectionsYet:
            'Chưa có phần nội dung nào. Thêm phần đầu tiên để bắt đầu.',
    },
};

registerSchemaI18n(defaultPageElementI18n);

export interface PageBlock {
    /** Unique block ID */
    id: string;
    /** Block content (text, image, audio, video, rich-text) */
    content: BlockContent;
    /** Optional block-level settings */
    settings?: PageBlockSettings;
}

/**
 * Block-level settings
 */
export interface PageBlockSettings {
    /** CSS class for custom styling */
    className?: string;
    /** Block visibility */
    hidden?: boolean;
    /** Only show this block after the activity has been submitted */
    showAfterSubmitted?: boolean;
    /** Block alignment within section */
    alignment?: 'left' | 'center' | 'right' | 'stretch';
    /** Block width (grid column span) */
    span?: number;
}

/**
 * A section within the page containing blocks
 */
export interface PageSection {
    /** Unique section ID */
    id: string;
    /** Optional section title */
    title?: string;
    /** Layout configuration for this section */
    layout: LayoutConfig;
    /** Blocks within this section */
    blocks: PageBlock[];
    /** Optional section-level settings */
    settings?: PageSectionSettings;
}

/**
 * Section-level settings
 */
export interface PageSectionSettings {
    /** CSS class for custom styling */
    className?: string;
    /** Section visibility */
    hidden?: boolean;
    /** Section background color */
    backgroundColor?: string;
    /** Section padding */
    padding?: string;
    /** Section margin */
    margin?: string;
    /** Whether the section is collapsible */
    collapsible?: boolean;
    /** Whether the section starts collapsed */
    defaultCollapsed?: boolean;
}

/**
 * Page Element Data
 */
export interface PageElementData {
    /** Optional page title */
    title?: string;
    /** Optional page description */
    description?: string;
    /** Sections in the page */
    sections: PageSection[];
    /** Default layout for new sections */
    defaultLayout?: LayoutConfig;
}

/**
 * Default layout configuration
 */
export const defaultPageLayout: LayoutConfig = {
    type: 'vertical',
    gap: '1rem',
    alignment: 'stretch',
};

/**
 * Default page data
 */
export const defaultPageData: PageElementData = {
    title: '',
    description: '',
    sections: [],
    defaultLayout: defaultPageLayout,
};

/**
 * Page element schema definition
 */
export const pageElementSchema = {
    type: 'page',
    title: 'Page',
    description: 'A page container with sections and content blocks',
    version: '1.0.0',
    category: 'layout',
    icon: 'LayoutTemplate',
    tags: ['page', 'layout', 'container', 'sections'],
    defaultData: defaultPageData,
    fields: [
        {
            name: 'title',
            type: 'text',
            label: 'Title',
            placeholder: 'Page title',
        },
        {
            name: 'description',
            type: 'textarea',
            label: 'Description',
            placeholder: 'Page description',
        },
        {
            name: 'sections',
            type: 'array',
            label: 'Sections',
            itemType: 'section',
        },
    ],
    i18n: defaultPageElementI18n,
};

/**
 * Layout presets for sections
 */
export const sectionLayoutPresets = {
    /** Single column, full width */
    single: {
        type: 'single' as const,
    },
    /** Vertical stack */
    vertical: {
        type: 'vertical' as const,
        gap: '1rem',
        alignment: 'stretch' as const,
    },
    /** Horizontal row */
    horizontal: {
        type: 'horizontal' as const,
        gap: '1rem',
        alignment: 'start' as const,
    },
    /** 2-column grid */
    grid2: {
        type: 'grid' as const,
        columns: 2,
        gap: '1rem',
    },
    /** 3-column grid */
    grid3: {
        type: 'grid' as const,
        columns: 3,
        gap: '1rem',
    },
    /** 4-column grid */
    grid4: {
        type: 'grid' as const,
        columns: 4,
        gap: '1rem',
    },
};

/**
 * Helper to create a new section
 */
export function createSection(
    id: string,
    layout: LayoutConfig = sectionLayoutPresets.vertical,
    title?: string
): PageSection {
    return {
        id,
        title,
        layout,
        blocks: [],
    };
}

/**
 * Helper to create a new block
 */
export function createBlock(id: string, content: BlockContent): PageBlock {
    return {
        id,
        content,
    };
}
